import React, { useEffect, useRef, useState } from "react";
import { apiGet, apiPost, apiPut, apiDelete } from "../../../services/api";
import DrugGroupMap from "./DrugGroupMap";
import { parseCsv, toCsv, downloadCsv } from "../../../utils/csv";
import { useToast } from "../../common/ToastProvider";
import Button from "../../common/Button";

interface DrugGroup {
  id?: number;
  group_id: number;
  group_name: string;
  name?: string;
  description?: string;
  item_count?: number;
}

interface FormState {
  name: string;
  description: string;
}

export default function DrugGroups() {
  const { showToast } = useToast();
  const [rows, setRows] = useState<DrugGroup[]>([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<DrugGroup | null>(null);
  const importRef = useRef<HTMLInputElement | null>(null);

  const [form, setForm] = useState<FormState>({ name: "", description: "" });
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);

  const load = async () => {
    try {
      const data = await apiGet("/api/admin/drug-groups") as (DrugGroup & { id?: number; name?: string; item_count?: number })[];
      setRows((Array.isArray(data) ? data : []).map((g) => ({
        group_id: g.id ?? g.group_id,
        group_name: g.name ?? g.group_name,
        description: g.description,
        item_count: g.item_count ?? 0,
      })));
    } catch (err) {
      showToast({ message: "Failed to load drug groups", type: "error" });
    }
  };

  useEffect(() => { load(); }, []);

  const handleExport = () => {
    if (!rows.length) {
      showToast({ message: "No data to export.", type: "warning" });
      return;
    }
    const headers = ["name", "description"];
    const data = rows.map((g) => ({
      name: g.group_name,
      description: g.description || "",
    }));
    downloadCsv(toCsv(headers, data), "drug_groups.csv");
    showToast({ message: "Export downloaded.", type: "info" });
  };

  const handleImportFile = async (file: File) => {
    const text = await file.text();
    const csvRows = parseCsv(text);
    if (csvRows.length < 2) return showToast({ message: "CSV is empty", type: "error" });
    const headers = csvRows[0].map((h) => h.trim().toLowerCase());
    const dataRows = csvRows.slice(1);

    showToast({ message: "Importing CSV data...", type: "info" });
    let errorCount = 0;

    for (const row of dataRows) {
      const get = (key: string) => {
        const idx = headers.indexOf(key);
        return idx >= 0 ? (row[idx] ?? "").trim() : "";
      };
      const name = get("name");
      if (!name) continue;
      const payload = {
        name,
        description: get("description") || undefined,
      };
      
      try {
        const existing = rows.find((g) => g.group_name === name);
        if (existing) {
          await apiPut(`/api/admin/drug-groups/${existing.group_id}`, payload);
        } else {
          await apiPost("/api/admin/drug-groups", payload);
        }
      } catch (e) {
        errorCount++;
        console.error("Row import failed", row, e);
      }
    }
    
    if (errorCount > 0) {
      showToast({ message: `Import finished with ${errorCount} errors.`, type: "warning" });
    } else {
      showToast({ message: "Import successful.", type: "success" });
    }
    load();
  };

  const handleSubmit = async () => {
    if (!form.name.trim()) return showToast({ message: "Group name required", type: "warning" });

    const payload = {
      name: form.name.trim(),
      description: form.description || undefined,
    };

    try {
      if (editingId) {
        await apiPut(`/api/admin/drug-groups/${editingId}`, payload);
        showToast({ message: "Group Updated", type: "success" });
      } else {
        await apiPost("/api/admin/drug-groups", payload);
        showToast({ message: "Group Created", type: "success" });
      }

      setForm({ name: "", description: "" });
      setEditingId(null);
      setShowForm(false);
      load();
    } catch (e) {
      showToast({ message: "Operation failed", type: "error" });
    }
  };

  const handleEdit = (group: DrugGroup) => {
    setForm({
      name: group.group_name,
      description: group.description || ""
    });
    setEditingId(group.group_id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setForm({ name: "", description: "" });
    setEditingId(null);
    setShowForm(false);
  };

  const remove = async (group: DrugGroup) => {
    if (!window.confirm(`Delete group "${group.group_name}"?`)) return;
    try {
      await apiDelete(`/api/admin/drug-groups/${group.group_id}`);
      showToast({ message: "Group deleted", type: "success" });
      load();
    } catch (e) {
      showToast({ message: "Failed to delete group", type: "error" });
    }
  };

  const filtered = rows.filter(g =>
    g.group_name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Toolbar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '16px', background: '#f8fafc', borderBottom: '1px solid #e2e8f0', gap: '16px', flexWrap: 'wrap' }}>
        <input
          type="search"
          placeholder="Search groups..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ padding: '10px 16px', borderRadius: '8px', border: '1px solid #cbd5e1', minWidth: '250px' }}
        />
        <div style={{ display: 'flex', gap: '12px' }}>
          <input
            ref={importRef}
            type="file"
            accept=".csv"
            style={{ display: "none" }}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const file = e.target.files?.[0];
              if (file) handleImportFile(file);
              if (e.target) e.target.value = "";
            }}
          />
          <Button variant="outline" onClick={() => importRef.current?.click()}>Import CSV</Button>
          <Button variant="outline" onClick={handleExport}>Export CSV</Button>
          <Button variant="primary" onClick={() => { setShowForm(!showForm); if (!showForm) { setForm({ name: "", description: "" }); setEditingId(null); } }}>
            {showForm ? "Cancel Form" : "+ Create Group"}
          </Button>
        </div>
      </div>

      {showForm && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr auto", gap: "12px", padding: '16px', background: editingId ? '#eff6ff' : '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
          <input
            placeholder="Group Name (e.g. RCT Kit)"
            value={form.name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, name: e.target.value })}
            style={{ padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
          />
          <input
            placeholder="Description"
            value={form.description}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, description: e.target.value })}
            style={{ padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1' }}
          />

          <div style={{ display: 'flex', gap: '8px' }}>
            <Button variant="primary" onClick={handleSubmit}>
              {editingId ? "💾 Update" : "➕ Create"}
            </Button>
            {editingId && (
              <Button variant="outline" onClick={cancelEdit}>Cancel</Button>
            )}
          </div>
        </div>
      )}

      {/* Data Table */}
      <div style={{ overflowX: 'auto', padding: '16px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem', textAlign: 'left' }}>
          <thead style={{ borderBottom: '2px solid #e2e8f0', color: '#64748b' }}>
            <tr>
              <th style={{ padding: '12px 8px' }}>Group Name</th>
              <th style={{ padding: '12px 8px' }}>Description</th>
              <th style={{ padding: '12px 8px' }}>Drugs</th>
              <th style={{ padding: '12px 8px' }}>Manage</th>
              <th style={{ padding: '12px 8px', textAlign: 'right' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={5} style={{ padding: '24px', textAlign: 'center', color: '#94a3b8' }}>No groups found</td></tr>
            ) : (
              filtered.map((g) => (
                <tr key={g.group_id} style={{ borderBottom: '1px solid #f1f5f9', background: editingId === g.group_id ? '#f0f9ff' : 'transparent' }}>
                  <td style={{ padding: '12px 8px', fontWeight: 600, color: '#0f172a' }}>{g.group_name}</td>
                  <td style={{ padding: '12px 8px' }}>{g.description || "—"}</td>
                  <td style={{ padding: '12px 8px' }}>
                    <span style={{ fontWeight: 600, background: '#e0f2fe', color: '#0369a1', padding: '4px 8px', borderRadius: '6px' }}>
                      {g.item_count || 0} items
                    </span>
                  </td>
                  <td style={{ padding: '12px 8px' }}>
                    <Button variant="secondary" size="sm" onClick={() => setSelected(g)}>
                      ⚙️ Config Drugs
                    </Button>
                  </td>
                  <td style={{ padding: '12px 8px', textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                      <Button variant="outline" size="sm" onClick={() => handleEdit(g)}>Edit Info</Button>
                      <Button variant="danger" size="sm" onClick={() => remove(g)}>Delete</Button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {selected && (
        <DrugGroupMap
          group={selected}
          onClose={() => { setSelected(null); load(); }}
        />
      )}
    </div>
  );
}
